import { AdminApiError, type AdminClient } from '@apx/admin-sdk';
import { crashMultipliersSchema, type CrashMultipliersOverride } from '@apx/integrator-config/schemas';
import { askPrompt } from '../modal.js';
import { fmtDateTime } from '../format.js';
import { showToast } from '../toast.js';

export interface CrashMultipliersPageDeps {
  client: AdminClient;
  integratorId: string;
  gameId: string;
  /** Whether the current scope is the provider baseline (vs. a specific integrator override). */
  isProviderScope: boolean;
}

/** Baked default — kept in sync with DEFAULT_RTP_BPS in packages/games/crash/src/crash-math.ts. */
const DEFAULT_RTP_BPS = 9600;
/** Mirrors the bounds on `crashMultipliersSchema` in @apx/integrator-config. */
const MIN_RTP_PCT = 50;
const MAX_RTP_PCT = 100;

function rtpColor(rtp: number): string {
  if (rtp >= 1.0) return 'var(--accent-red)';
  if (rtp > 0.97) return 'var(--accent-amber)';
  return 'var(--accent-green)';
}

/** Percent input → bps. Two decimals of percent is exactly one bps. */
function pctToBps(pct: number): number {
  return Math.round(pct * 100);
}

/**
 * Crash RTP editor. Crash has no paytable — the player names the multiplier —
 * so its only tunable is the RTP constant `k` itself (`multipliers.rtpBps`).
 * `P(reach x) = k / x` and a win pays `x`, so every target returns exactly `k`:
 * one number, no per-bet breakdown to balance. Edited as a percentage, stored as
 * bps. Changes ride the two-admin approval flow.
 *
 * Without this page crash fell through to greedyking's food-multiplier editor,
 * whose save stored carrots/steak rows on crash and never reached `rtpBps`.
 */
export async function renderCrashMultipliersPage(
  root: HTMLElement,
  deps: CrashMultipliersPageDeps,
): Promise<void> {
  root.innerHTML = `
    <h1 class="page-title">RTP — Crash</h1>
    <p class="page-sub">Crash pays whatever multiplier the player names, so there is no paytable. The single setting below <strong>is</strong> the RTP: every target, 1.01× to 10,000×, returns exactly this figure. Changes go through two-admin approval.</p>

    <div class="card">
      <h2>Return to player</h2>
      <table>
        <thead><tr><th>Setting</th><th>RTP (%)</th><th>House edge</th><th></th></tr></thead>
        <tbody>
          <tr>
            <td><strong>RTP constant <code>k</code></strong><br><span class="muted">Same at every target · ${MIN_RTP_PCT}–${MAX_RTP_PCT}%</span></td>
            <td><input id="crash-rtp" type="number" min="${MIN_RTP_PCT}" max="${MAX_RTP_PCT}" step="0.01" style="width:120px" /></td>
            <td class="muted" id="crash-rtp-hint">—</td>
            <td class="muted" data-default>default ${(DEFAULT_RTP_BPS / 100).toFixed(2)}%</td>
          </tr>
        </tbody>
      </table>
      <div class="row" style="margin-top:14px">
        <button id="crash-reset" class="ghost">Reset to default</button>
        <button id="crash-save" class="primary">Propose change</button>
        <span id="crash-msg" class="muted"></span>
      </div>
    </div>

    <div class="card">
      <h2>Pending approvals for this surface</h2>
      <div id="crash-pending"><p class="muted">Loading…</p></div>
    </div>
  `;

  const rtpEl = root.querySelector<HTMLInputElement>('#crash-rtp')!;
  const hintEl = root.querySelector<HTMLElement>('#crash-rtp-hint')!;
  const msgEl = root.querySelector<HTMLElement>('#crash-msg')!;

  function updateHint(): void {
    const rtp = pctToBps(Number(rtpEl.value) || 0) / 10000;
    hintEl.innerHTML = `<strong style="color:${rtpColor(rtp)}">${((1 - rtp) * 100).toFixed(2)}%</strong>`;
  }
  rtpEl.addEventListener('input', updateHint);

  function readForm(): CrashMultipliersOverride {
    return { version: 1, rtpBps: pctToBps(Number(rtpEl.value) || 0) };
  }

  function fillForm(value: CrashMultipliersOverride | null): void {
    const bps = typeof value?.rtpBps === 'number' ? value.rtpBps : DEFAULT_RTP_BPS;
    rtpEl.value = (bps / 100).toFixed(2);
    updateHint();
  }

  const { value: current } = await deps.client.getSurface(deps.integratorId, deps.gameId, 'multipliers');
  fillForm(current as CrashMultipliersOverride | null);

  root.querySelector<HTMLButtonElement>('#crash-reset')!.addEventListener('click', () => {
    fillForm(null);
    msgEl.textContent = 'Reset to module default (not saved).';
    msgEl.className = 'muted';
  });

  root.querySelector<HTMLButtonElement>('#crash-save')!.addEventListener('click', async () => {
    msgEl.textContent = '';
    const parsed = crashMultipliersSchema.safeParse(readForm());
    if (!parsed.success) {
      msgEl.textContent = `RTP must be ${MIN_RTP_PCT}–${MAX_RTP_PCT}%, to two decimals.`;
      msgEl.className = 'error';
      return;
    }
    try {
      const res = await deps.client.proposeSurface(deps.integratorId, deps.gameId, 'multipliers', parsed.data);
      msgEl.textContent = `Pending approval ${res.approval.id}. A different admin must approve.`;
      msgEl.className = 'muted';
      await refreshPending();
    } catch (err) {
      msgEl.textContent = err instanceof AdminApiError ? `${err.code}: ${err.message}` : String(err);
      msgEl.className = 'error';
    }
  });

  async function refreshPending(): Promise<void> {
    const all = await deps.client.listApprovals(deps.integratorId, 'pending');
    const subset = all.filter((a) => a.surface === 'multipliers' && a.gameId === deps.gameId);
    const container = root.querySelector<HTMLElement>('#crash-pending')!;
    if (subset.length === 0) {
      container.innerHTML = '<p class="muted">No pending changes.</p>';
      return;
    }
    container.innerHTML = `<table>
      <thead><tr><th>Created</th><th>Proposer</th><th>Proposed RTP</th><th></th></tr></thead>
      <tbody>${subset
        .map((a) => {
          const bps = (a.pendingConfig as { rtpBps?: unknown } | null)?.rtpBps;
          const shown = typeof bps === 'number' ? `${(bps / 100).toFixed(2)}%` : '—';
          return `<tr>
            <td>${fmtDateTime(a.createdAt)}</td>
            <td><code>${a.proposerUserId.slice(0, 8)}…</code></td>
            <td>${shown}</td>
            <td>
              <button data-approve="${a.id}">Approve</button>
              <button data-reject="${a.id}" class="ghost">Reject</button>
            </td>
          </tr>`;
        })
        .join('')}</tbody>
    </table>`;
    container.querySelectorAll<HTMLButtonElement>('button[data-approve]').forEach((btn) => {
      btn.addEventListener('click', async () => {
        try {
          await deps.client.approve(btn.dataset.approve!);
          msgEl.textContent = 'Approved — new RTP applies from the next round.';
          msgEl.className = 'muted';
          await refreshPending();
          const fresh = await deps.client.getSurface(deps.integratorId, deps.gameId, 'multipliers');
          fillForm(fresh.value as CrashMultipliersOverride | null);
        } catch (err) {
          showToast(err instanceof AdminApiError ? `${err.code}: ${err.message}` : String(err), 'error');
        }
      });
    });
    container.querySelectorAll<HTMLButtonElement>('button[data-reject]').forEach((btn) => {
      btn.addEventListener('click', async () => {
        const reason = await askPrompt('Rejection reason?');
        if (!reason) return;
        try {
          await deps.client.reject(btn.dataset.reject!, reason);
          await refreshPending();
        } catch (err) {
          showToast(err instanceof AdminApiError ? `${err.code}: ${err.message}` : String(err), 'error');
        }
      });
    });
  }

  await refreshPending();
}
